import { Document, Packer, Paragraph, Table, TableRow, TableCell, AlignmentType, BorderStyle, HeadingLevel, WidthType, TableLayoutType, ImageRun } from 'docx';
import { saveAs } from 'file-saver';
import moment from 'moment';
import Logo from "../../../../styles/img/CompliNYC-Logo.png";

export const generateNoAccessDocument = async (clientData) => {
  if (!clientData || !clientData.buildings || clientData.buildings.length === 0) {
    throw new Error("No data found for selected client or no buildings available");
  }

  // Create document
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          // Add a logo image at the top center of the document
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              // The image may not be showing because the Logo import is a path to an image file,
              // but when using fetch(Logo), the path may not resolve correctly in the build/output,
              // or the image may not be accessible due to CORS or file-loader config.
              // A more robust approach is to use a base64-encoded image string.
              // For demonstration, here's how you could embed a base64 image directly:

              // Example: Replace Logo with a base64 string (you can generate this from your image)
              // const logoBase64 = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...";
              // new ImageRun({
              //   data: Buffer.from(logoBase64.split(",")[1], "base64"),
              //   transformation: { width: 120, height: 60 },
              // }),

              // If you want to keep using fetch, ensure Logo is a public URL accessible at runtime.
              // For create-react-app, place the image in /public and use process.env.PUBLIC_URL:
              // const logoUrl = process.env.PUBLIC_URL + "/CompliNYC-Logo.png";
              // new ImageRun({
              //   data: await fetch(logoUrl).then(res => res.arrayBuffer()),
              //   transformation: { width: 120, height: 60 },
              // }),

              // If you want to keep the original code, but ensure Logo is a valid URL:
              new ImageRun({
                data: await fetch(Logo).then((res) => res.arrayBuffer()),
                transformation: { width: 180, height: 60 },
              }),
            ],
            spacing: { after: 200 },
          }),
          new Paragraph({
            text: `No Access Report – Required XRF Lead Testing Incomplete`,
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            bold: true,
            size: 27, // 13.5pt in half-points
          }),
          new Paragraph({
            text: `Dear ${clientData.clientName},`,
            spacing: {
              after: 200,
            },
          }),
          new Paragraph({
            text: `This report summarizes units at your properties that have missed scheduled XRF lead-based paint inspections as required under Local Law 31. As of today's date (${moment().format(
              "MM/DD/YYYY"
            )}), these units have not completed the required inspection due to tenant failure to provide access on scheduled dates.`,
            spacing: {
              after: 200,
            },
          }),

          // Create tables for each building
          ...clientData.buildings.flatMap((building) => [
            new Paragraph({
              text: `Property: ${building.name}`,
              bold: true,
              spacing: {
                before: 300,
                after: 200,
              },
            }),

            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              layout: TableLayoutType.FIXED,
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "#000000" },
                bottom: {
                  style: BorderStyle.SINGLE,
                  size: 1,
                  color: "#000000",
                },
                left: { style: BorderStyle.SINGLE, size: 1, color: "#000000" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "#000000" },
                insideHorizontal: {
                  style: BorderStyle.SINGLE,
                  size: 1,
                  color: "#000000",
                },
                insideVertical: {
                  style: BorderStyle.SINGLE,
                  size: 1,
                  color: "#000000",
                },
              },
              columnWidths: [1000, 1500, 1500, 1500],
              rows: [
                // Header row
                new TableRow({
                  tableHeader: true,
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          text: "Unit",
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 50, after: 50 },
                        }),
                      ],
                      shading: { fill: "D3D3D3" },
                      verticalAlign: "center",
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          text: "1st Attempt",
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 50, after: 50 },
                        }),
                      ],
                      shading: { fill: "D3D3D3" },
                      verticalAlign: "center",
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          text: "2nd Attempt",
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 50, after: 50 },
                        }),
                      ],
                      shading: { fill: "D3D3D3" },
                      verticalAlign: "center",
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          text: "Status",
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 50, after: 50 },
                        }),
                      ],
                      shading: { fill: "D3D3D3" },
                      verticalAlign: "center",
                    }),
                  ],
                }),
                // Data rows
                ...building.units.map(
                  (unit) =>
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              text: unit.unit,
                              spacing: { before: 50, after: 50 },
                            }),
                          ],
                          verticalAlign: "center",
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              text: moment(unit.firstAttempt).format(
                                "MM/DD/YYYY"
                              ),
                              spacing: { before: 50, after: 50 },
                            }),
                          ],
                          verticalAlign: "center",
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              text: unit.secondAttempt
                                ? moment(unit.secondAttempt).format(
                                    "MM/DD/YYYY"
                                  )
                                : "",
                              spacing: { before: 50, after: 50 },
                            }),
                          ],
                          verticalAlign: "center",
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              text: unit.status,
                              spacing: { before: 50, after: 50 },
                            }),
                          ],
                          verticalAlign: "center",
                        }),
                      ],
                    })
                ),
              ],
            }),
          ]),

          new Paragraph({
            text: "Important Compliance Notice",
            heading: HeadingLevel.HEADING_2,
            spacing: {
              before: 400,
              after: 200,
            },
          }),
          new Paragraph({
            text: `These units remain non-compliant with the NYC Local Law 31 requirement to conduct lead-based paint XRF inspections by the legal deadline of August 9, 2025.`,
            spacing: {
              after: 200,
            },
          }),
          new Paragraph({
            text: `If any unit is not tested by this deadline, the NYC Department of Housing Preservation and Development (HPD) may issue a penalty of $1,500.00 per untested unit, regardless of the reason access was not provided.`,
            spacing: {
              after: 200,
            },
          }),
          new Paragraph({
            text: "Please be advised:",
            spacing: {
              after: 200,
            },
          }),
          new Paragraph({
            text: "• The responsibility to gain access falls solely on the property owner.",
            spacing: {
              after: 100,
            },
          }),
          new Paragraph({
            text: "• As the inspection firm, our role is limited to attempting access and conducting testing when permitted.",
            spacing: {
              after: 100,
            },
          }),
          new Paragraph({
            text: "• We strongly recommend that you communicate directly with your tenants and enforce the access provisions of your lease agreement.",
            spacing: {
              after: 200,
            },
          }),
          new Paragraph({
            text: "Next Steps",
            heading: HeadingLevel.HEADING_2,
            spacing: {
              after: 200,
            },
          }),
          new Paragraph({
            text: "• Reschedule missed units as soon as possible.",
            spacing: {
              after: 100,
            },
          }),
          new Paragraph({
            text: "• Send tenants written notice via proof of mailing and or certified mail to tenants reinforcing their legal obligation to provide access for this required inspection. ",
            spacing: {
              after: 200,
            },
          }),
          new Paragraph({
            text: "If you have any questions, please contact us at 347-734-1865",
            spacing: {
              after: 200,
            },
          }),
        ],
      },
    ],
  });

  // Generate document
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${clientData.clientName}_Missed_Access_Report.docx`);
}; 